import { z } from 'zod';
import type {
  ApplicantInput,
  CalculatePlanInput,
  ConditionsInput,
  DocumentType,
  FlowDraft,
  PlanConditionsInput,
} from '../../types/separations';

/**
 * Validación de UX del flujo del asesor (web 02). El back vuelve a validar y manda (AGENTS §24).
 * Los campos se manejan como texto en el formulario y se convierten al contrato al enviar.
 */

export const TIPOS_DOCUMENTO_NATURAL: Array<{ value: Exclude<DocumentType, 'NIT'>; label: string }> = [
  { value: 'CC', label: 'Cédula de ciudadanía' },
  { value: 'CE', label: 'Cédula de extranjería' },
  { value: 'PASAPORTE', label: 'Pasaporte' },
  { value: 'TI', label: 'Tarjeta de identidad' },
];

export const ESTADOS_CIVILES = [
  'Soltero(a)',
  'Casado(a)',
  'Unión marital de hecho',
  'Divorciado(a)',
  'Viudo(a)',
] as const;

const SOLO_DIGITOS = /^\d+$/;

const applicantSchema = z
  .object({
    tipoPersona: z.enum(['NATURAL', 'JURIDICA']),
    nombres: z.string().trim(),
    apellidos: z.string().trim(),
    razonSocial: z.string().trim(),
    tipoDocumento: z.enum(['CC', 'CE', 'NIT', 'PASAPORTE', 'TI']),
    numeroDocumento: z.string().trim(),
    ciudadExpedicion: z.string().trim(),
    estadoCivil: z.string().trim(),
    direccion: z.string().trim().min(1, 'Ingrese la dirección'),
    ciudad: z.string().trim().min(1, 'Ingrese la ciudad'),
    celular: z.string().trim().min(1, 'Ingrese el celular'),
    correo: z.string().trim().min(1, 'Ingrese el correo').email('Ingrese un correo válido'),
    porcentaje: z.string().trim(),
  })
  .superRefine((s, ctx) => {
    const requerir = (campo: keyof typeof s, mensaje: string): void => {
      if (!String(s[campo]).trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [campo], message: mensaje });
      }
    };
    if (s.tipoPersona === 'NATURAL') {
      requerir('nombres', 'Ingrese los nombres');
      requerir('apellidos', 'Ingrese los apellidos');
      requerir('ciudadExpedicion', 'Ingrese la ciudad de expedición');
      requerir('estadoCivil', 'Seleccione el estado civil');
    } else {
      requerir('razonSocial', 'Ingrese la razón social');
      requerir('nombres', 'Ingrese los nombres del representante legal');
      requerir('apellidos', 'Ingrese los apellidos del representante legal');
    }
    if (!s.numeroDocumento) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['numeroDocumento'],
        message: s.tipoPersona === 'JURIDICA' ? 'Ingrese el NIT' : 'Ingrese el número de documento',
      });
    } else if (!SOLO_DIGITOS.test(s.numeroDocumento)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['numeroDocumento'],
        message: 'El documento debe contener solo dígitos',
      });
    }
  });

export const applicantsFormSchema = z
  .object({
    separantes: z.array(applicantSchema).min(1),
    autorizacion: z.boolean(),
  })
  .superRefine((form, ctx) => {
    if (!form.autorizacion) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['autorizacion'],
        message: 'Debe registrar la autorización de tratamiento de datos para continuar',
      });
    }
    if (form.separantes.length < 2) return;
    let validos = true;
    form.separantes.forEach((s, index) => {
      const valor = Number(s.porcentaje);
      if (!SOLO_DIGITOS.test(s.porcentaje) || valor < 1 || valor > 100) {
        validos = false;
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['separantes', index, 'porcentaje'],
          message: 'Ingrese un porcentaje entero entre 1 y 100',
        });
      }
    });
    if (!validos) return;
    const suma = form.separantes.reduce((total, s) => total + Number(s.porcentaje), 0);
    if (suma !== 100) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['separantes', form.separantes.length - 1, 'porcentaje'],
        message: `Los porcentajes deben sumar 100 % (hoy suman ${suma} %)`,
      });
    }
  });

export type ApplicantsFormValues = z.infer<typeof applicantsFormSchema>;
export type ApplicantFormValues = ApplicantsFormValues['separantes'][number];

export function emptyApplicant(porcentaje = ''): ApplicantFormValues {
  return {
    tipoPersona: 'NATURAL',
    nombres: '',
    apellidos: '',
    razonSocial: '',
    tipoDocumento: 'CC',
    numeroDocumento: '',
    ciudadExpedicion: '',
    estadoCivil: '',
    direccion: '',
    ciudad: '',
    celular: '',
    correo: '',
    porcentaje,
  };
}

/** Formulario → contrato `SeparanteDto` (omite los campos que no aplican al tipo de persona). */
export function toApplicantInputs(values: ApplicantsFormValues): ApplicantInput[] {
  const unico = values.separantes.length === 1;
  return values.separantes.map((s) => {
    const comun = {
      numeroDocumento: s.numeroDocumento.trim(),
      nombres: s.nombres.trim(),
      apellidos: s.apellidos.trim(),
      direccion: s.direccion.trim(),
      ciudad: s.ciudad.trim(),
      celular: s.celular.trim(),
      correo: s.correo.trim(),
      porcentaje: unico ? 100 : Number(s.porcentaje),
    };
    if (s.tipoPersona === 'JURIDICA') {
      return {
        tipoPersona: 'JURIDICA',
        razonSocial: s.razonSocial.trim(),
        tipoDocumento: 'NIT',
        ...comun,
      };
    }
    return {
      tipoPersona: 'NATURAL',
      tipoDocumento: s.tipoDocumento,
      ciudadExpedicion: s.ciudadExpedicion.trim(),
      estadoCivil: s.estadoCivil.trim(),
      ...comun,
    };
  });
}

export const MIN_CUOTAS = 1;
export const MAX_CUOTAS = 60;

export const conditionsSchema = z
  .object({
    precioNegociado: z.string(),
    valorSeparacion: z.string(),
    modoCuotaInicial: z.enum(['PORCENTAJE', 'VALOR']),
    porcentajeCuotaInicial: z.number(),
    valorCuotaInicial: z.string(),
    numeroCuotas: z.string().trim(),
    diaPago: z.string().trim(),
    fechaSeparacion: z.string().min(1, 'Ingrese la fecha de separación'),
    fechaEscrituracion: z.string().min(1, 'Ingrese la fecha estimada de escrituración'),
  })
  .superRefine((c, ctx) => {
    const issue = (path: string, message: string): void => {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
    };
    if (!SOLO_DIGITOS.test(c.precioNegociado) || BigInt(c.precioNegociado) <= 0n) {
      issue('precioNegociado', 'Ingrese el precio negociado de la unidad (mayor a cero)');
    }
    if (!SOLO_DIGITOS.test(c.valorSeparacion)) {
      issue('valorSeparacion', 'Ingrese el valor de separación');
    }
    if (c.modoCuotaInicial === 'PORCENTAJE' && !(c.porcentajeCuotaInicial > 0)) {
      issue('porcentajeCuotaInicial', 'Ingrese el porcentaje de cuota inicial');
    }
    if (c.modoCuotaInicial === 'VALOR' && !SOLO_DIGITOS.test(c.valorCuotaInicial)) {
      issue('valorCuotaInicial', 'Ingrese el valor de la cuota inicial');
    }
    const cuotas = Number(c.numeroCuotas);
    if (!SOLO_DIGITOS.test(c.numeroCuotas) || cuotas < MIN_CUOTAS || cuotas > MAX_CUOTAS) {
      issue('numeroCuotas', `El número de cuotas debe estar entre ${MIN_CUOTAS} y ${MAX_CUOTAS}`);
    }
    const dia = Number(c.diaPago);
    if (!SOLO_DIGITOS.test(c.diaPago) || dia < 1 || dia > 31) {
      issue('diaPago', 'El día de pago debe estar entre 1 y 31');
    }
  });

export type ConditionsFormValues = z.infer<typeof conditionsSchema>;

/**
 * Formulario → condiciones con los nombres del motor (preview y generación, feat-26). fix-50 (B-02):
 * `negotiatedPrice` es el negociado de la unidad principal. Solo viaja una forma de cuota inicial.
 */
export function toPlanConditions(values: ConditionsFormValues): PlanConditionsInput {
  const base: PlanConditionsInput = {
    negotiatedPrice: values.precioNegociado,
    separationValue: values.valorSeparacion,
    numberOfInstallments: Number(values.numeroCuotas),
    separationDate: values.fechaSeparacion,
    paymentDay: Number(values.diaPago),
    estimatedDeedDate: values.fechaEscrituracion,
  };
  return values.modoCuotaInicial === 'PORCENTAJE'
    ? { ...base, initialPaymentPercent: values.porcentajeCuotaInicial }
    : { ...base, initialPaymentValue: values.valorCuotaInicial };
}

/**
 * Formulario → `CalculatePaymentPlanDto`. La unidad y las adicionales van por id: el back lee sus
 * precios de lista de BD y compone el total (fix-50 · ADR-017); ya no se envía `priceList`.
 */
export function toCalculateInput(values: ConditionsFormValues, draft: FlowDraft): CalculatePlanInput {
  return {
    unitId: draft.unidad.id,
    additionalUnitIds: draft.adicionales.map((u) => u.id),
    ...toPlanConditions(values),
  };
}

/** Formulario → `PUT /separations/:id/condiciones` (fix-53 · B-03; fix-50 · B-02; fix-64: plazo y día). */
export function toConditionsInput(values: ConditionsFormValues): ConditionsInput {
  const base: ConditionsInput = {
    precioNegociadoPrincipal: values.precioNegociado,
    valorSeparacion: values.valorSeparacion,
    numeroCuotas: Number(values.numeroCuotas),
    diaPago: Number(values.diaPago),
    fechaSeparacion: values.fechaSeparacion,
    fechaEscrituracion: values.fechaEscrituracion,
  };
  return values.modoCuotaInicial === 'PORCENTAJE'
    ? { ...base, cuotaInicialPorcentaje: values.porcentajeCuotaInicial }
    : { ...base, cuotaInicialValor: values.valorCuotaInicial };
}
